import { useState, useCallback, useEffect } from 'react';
import { adminApiClient, User } from '../../lib/api';
import { TempleRegistration } from './types';
import { toast } from 'react-hot-toast';
import Swal from 'sweetalert2';
import { SortingState, PaginationState } from '@tanstack/react-table';
import { useAuth } from '../../contexts/AuthContext';

export const useTempleRegistrations = () => {
    const { user } = useAuth();
    
    // UI State
    const [activeTab, setActiveTab] = useState<'new' | 'under_review' | 'setup_pending' | 'existing' | 'rejected'>('new');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Data State
    const [registrations, setRegistrations] = useState<TempleRegistration[]>([]);
    const [admins, setAdmins] = useState<User[]>([]);

    // Table State
    const [sorting, setSorting] = useState<SortingState>([{ id: 'createdAt', desc: true }]);
    const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [filters, setFilters] = useState({ search: '' });

    // Modal State
    const [selectedRegistration, setSelectedRegistration] = useState<TempleRegistration | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    
    // Action Modal State
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    
    // Subscription State
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [subscriptionPlans, setSubscriptionPlans] = useState<any[]>([]);
    const [templeSubscription, setTempleSubscription] = useState<any>(null);
    const [selectedPlanId, setSelectedPlanId] = useState<string>('');
    const [templeId, setTempleId] = useState<string | null>(null);
    const [searchingTemple, setSearchingTemple] = useState(false);

    // Assignment
    const [selectedAdminId, setSelectedAdminId] = useState<string>('');

    // History
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [applicationHistory, setApplicationHistory] = useState<any[]>([]);
    const [fetchingHistory, setFetchingHistory] = useState(false);
    const [historyTempleName, setHistoryTempleName] = useState('');

    // Fetch Admins
    useEffect(() => {
        if (activeTab === 'new' || activeTab === 'under_review') {
            adminApiClient.getUsers({ role: 'admin', limit: 100 }).then(res => {
                if (res.success && res.data) setAdmins(res.data.users);
            });
        }
    }, [activeTab]);

    // Fetch Registrations
    const fetchRegistrations = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const params: any = {
                page: pagination.pageIndex + 1,
                limit: pagination.pageSize,
                sortBy: sorting.length > 0 ? sorting[0].id : 'createdAt',
                sortOrder: sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : 'desc'
            };

            if (filters.search) params.search = filters.search;

            switch (activeTab) {
                case 'new':
                    params.status = 'pending';
                    params.assignedTo = 'null';
                    break;
                case 'under_review':
                    params.status = 'pending';
                    params.assignedTo = 'exists';
                    break;
                case 'setup_pending':
                    params.status = 'approved';
                    params.setupStatus = 'ne:on_boarded';
                    break;
                case 'existing':
                    params.status = 'approved';
                    params.setupStatus = 'on_boarded';
                    break;
                case 'rejected':
                    params.status = 'rejected';
                    break;
            }

            const response = await adminApiClient.getTempleRegistrations(params);

            if (response.success && response.data) {
                setRegistrations(response.data.registrations);
                const dataPagination = response.data.pagination || {};
                setTotalPages(dataPagination.pages || 0);
                setTotalRecords(dataPagination.total || 0);
            } else {
                setError(response.message || 'Failed to fetch temple registrations');
            }
        } catch (err: any) {
            setError(err.message || 'Error fetching temple registrations');
        } finally {
            setLoading(false);
        }
    }, [pagination.pageIndex, pagination.pageSize, sorting, filters, activeTab]);

    useEffect(() => {
        fetchRegistrations();
    }, [fetchRegistrations]);

    // Subscription Logic
    useEffect(() => {
        adminApiClient.getSubscriptionPlans().then(res => {
             if (res.success && res.data) setSubscriptionPlans(res.data.plans || []);
        }).catch(() => {});
    }, []);

    const fetchTempleSubscription = async (tid: string) => {
        try {
            const response = await adminApiClient.getTempleSubscriptionInfo(tid);
            if (response.success && response.data) setTempleSubscription(response.data);
        } catch {
            setTempleSubscription(null);
        }
    };

    const [templeDetails, setTempleDetails] = useState<any>(null);

    const fetchTempleDetails = async (tid: string) => {
        try {
            const response = await adminApiClient.getTempleById(tid);
            if (response.success && response.data) setTempleDetails(response.data.temple);
        } catch {
            setTempleDetails(null);
        }
    };

    const findAndSetTempleId = async (registration: TempleRegistration) => {
        setSearchingTemple(true);
        let templeIdValue: string | null = null;
        try {
            if (registration.createdTemple) {
                if (typeof registration.createdTemple === 'object' && registration.createdTemple._id) {
                    templeIdValue = registration.createdTemple._id;
                } else if (typeof registration.createdTemple === 'string') {
                    templeIdValue = registration.createdTemple;
                }
            }
            if (!templeIdValue) {
                try {
                     const templesResponse = await adminApiClient.getTemples({ search: registration.user.email, limit: 5 });
                      if (templesResponse.success && templesResponse.data?.temples) {
                            const temple = templesResponse.data.temples.find(
                                (t: any) => t.contact?.email && t.contact.email.toLowerCase() === registration.user.email.toLowerCase()
                            );
                            if (temple?._id) templeIdValue = temple._id;
                      }
                       if (!templeIdValue) {
                            const nameRes = await adminApiClient.getTemples({ search: registration.temple.name, limit: 5 });
                            if (nameRes.success && nameRes.data?.temples && nameRes.data.temples.length > 0) {
                                templeIdValue = nameRes.data.temples[0]._id;
                            }
                       }
                } catch (err) { console.error("Error searching for temple:", err); }
            }
            if (templeIdValue) {
                setTempleId(templeIdValue);
                await fetchTempleSubscription(templeIdValue);
                await fetchTempleDetails(templeIdValue);
            } else {
                setTempleId(null);
                setTempleDetails(null);
            }
        } finally {
            setSearchingTemple(false);
        }
    };

    // Actions
    const handleViewDetails = async (registration: TempleRegistration) => {
        setSelectedRegistration(registration);
        setShowDetailsModal(true);
        setTempleId(null);
        setTempleSubscription(null);
        if (registration.status === 'approved') await findAndSetTempleId(registration);
    };

    const handleViewHistory = async (registration: TempleRegistration) => {
        setHistoryTempleName(registration.temple.name);
        setShowHistoryModal(true);
        setFetchingHistory(true);
        try {
            const response = await adminApiClient.getTempleRegistrationHistory(registration._id);
            if (response.success && response.data?.history) {
                setApplicationHistory(response.data.history);
            } else {
                toast.error(response.message || 'Failed to load history');
                setApplicationHistory([]);
            }
        } catch (err: any) {
            console.error('Failed to fetch history:', err);
            toast.error(err?.response?.data?.message || err.message || 'Error loading history');
            setApplicationHistory([]);
        } finally {
            setFetchingHistory(false);
        }
    };

    const handleAssignSubscription = async () => {
        if (!templeId || !selectedPlanId) return;
        try {
            setLoading(true);
            const response = await adminApiClient.assignSubscriptionToTemple(
                templeId, 
                selectedPlanId,
                selectedRegistration?._id // Pass explicitly to ensure update
            );
            if (response.success) {
                await fetchTempleSubscription(templeId);
                setShowSubscriptionModal(false);
                setSelectedPlanId('');
                toast.success('Subscription assigned successfully');
                fetchRegistrations();
            } else {
                toast.error(response.message || 'Failed to assign subscription plan');
            }
        } catch (err: any) {
            toast.error(err.message || 'Error assigning subscription plan');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveSubscription = async () => {
        if (!templeId) return;
        if (!window.confirm('Are you sure you want to remove the subscription from this temple? The temple will need to purchase a new subscription to continue.')) return;
        try {
            setLoading(true);
            const response = await adminApiClient.removeSubscriptionFromTemple(templeId);
            if (response.success) {
                await fetchTempleSubscription(templeId);
                toast.success('Subscription removed successfully');
                fetchRegistrations();
            } else {
                toast.error(response.message || 'Failed to remove subscription');
            }
        } catch (err: any) {
            toast.error(err.message || 'Error removing subscription');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (registration: TempleRegistration) => {
        try {
            setLoading(true);
            const response = await adminApiClient.approveTempleRegistration(registration._id);
            if (response.success) {
                let templeIdValue: string | null = null;
                if (response.data?.temple?.id || response.data?.temple?._id) {
                     templeIdValue = response.data.temple.id || response.data.temple._id;
                    if(templeIdValue) setTempleId(templeIdValue);
                }
                if (templeIdValue) await fetchTempleSubscription(templeIdValue);

                const updatedRegistration = { ...registration, status: 'approved' as const };
                if (templeIdValue && response.data?.temple) {
                     updatedRegistration.createdTemple = {
                         _id: templeIdValue,
                         name: response.data.temple.name || registration.temple.name,
                         slug: response.data.temple.slug || ''
                     };
                }
                setRegistrations(prev => prev.map(r => r._id === registration._id ? updatedRegistration : r));
                if (selectedRegistration?._id === registration._id) setSelectedRegistration(updatedRegistration);

                toast.success('Registration approved successfully');
                fetchRegistrations();
            } else {
                toast.error(response.message || 'Failed to approve registration');
            }
        } catch (err: any) {
            const errorMsg = err?.response?.data?.message || err.message || 'Error approving registration';
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleReviewAction = (approve: boolean) => {
        if (!selectedRegistration) return;
        if (approve) {
             const v = selectedRegistration.verificationStatus;
             const anyRejected = v?.certificate501c3?.status === 'rejected' || v?.stateVerification?.status === 'rejected' || v?.irsPropublica?.status === 'rejected';
             const allVerified = v?.certificate501c3?.status === 'verified' && v?.stateVerification?.status === 'verified' && v?.irsPropublica?.status === 'verified';

             if (anyRejected) {
                 setRejectionReason('One or more verification steps were rejected.');
                 setShowRejectModal(true);
             } else if (!allVerified) {
                 toast.error('All 3 verification steps must be VERIFIED before approval.');
             } else {
                 handleApprove(selectedRegistration);
             }
        } else {
            setRejectionReason('');
            setShowRejectModal(true);
        }
    };

    const handleAssignToMe = async (registration: TempleRegistration) => {
        const userId = user?.id || user?._id;
        if (!userId) {
            console.error("User ID not found", user);
            return;
        }
        try {
            setLoading(true);
            const response = await adminApiClient.assignRegistration(registration._id, userId);
            if (response.success) {
                const updatedAssignedTo = { _id: userId, name: user!.name, email: user!.email };
                setRegistrations(prev => prev.map(r => r._id === registration._id ? { ...r, assignedTo: updatedAssignedTo } : r));
                if (selectedRegistration?._id === registration._id) {
                    setSelectedRegistration(null);
                    setShowDetailsModal(false);
                }
                toast.success('Assigned to you');
                fetchRegistrations(); // Refresh list to move from New -> Under Review
            } else {
                toast.error(response.message || 'Failed to assign registration');
            }
        } catch (err: any) { toast.error(err.message || 'Error assigning registration'); } finally { setLoading(false); }
    };

    const handleAssignTo = async (registrationId: string, adminId: string): Promise<boolean> => {
        try {
            setLoading(true);
            const response = await adminApiClient.assignRegistration(registrationId, adminId);
            if (response.success) {
                 const assignedAdmin = admins.find(a => a._id === adminId || a.id === adminId);
                 const updatedAssignedTo = assignedAdmin ? { 
                     _id: assignedAdmin._id || assignedAdmin.id, 
                     name: assignedAdmin.name, 
                     email: assignedAdmin.email 
                 } : undefined;
                  setRegistrations(prev => prev.map(r => r._id === registrationId ? { ...r, assignedTo: updatedAssignedTo } : r));
                 if (selectedRegistration?._id === registrationId) setSelectedRegistration(prev => prev ? { ...prev, assignedTo: updatedAssignedTo } : null);
                toast.success('Assigned successfully');
                fetchRegistrations(); // Refresh list
                return true;
            } else {
                toast.error(response.message || 'Failed to assign');
                return false;
            }
        } catch (err: any) { toast.error(err.message || 'Error assigning'); return false; } finally { setLoading(false); }
    }

    const handleUpdateVerification = async (registrationId: string, type: string, status: string, notes?: string, screenshot?: string) => {
        try {
            const response = await adminApiClient.updateVerificationStatus(registrationId, { type, status, notes, screenshot });
            if (response.success) {
                const updatedRegistration = response.data?.registration;
                if (!updatedRegistration) return;
                setRegistrations(prev => prev.map(r => r._id === registrationId ? updatedRegistration : r));
                if (selectedRegistration?._id === registrationId) {
                    setSelectedRegistration(updatedRegistration);
                    if (updatedRegistration.status === 'rejected' && selectedRegistration.status === 'pending') {
                         toast.error('Registration REJECTED because a verification step was rejected.');
                    }
                }
                toast.success('Verification status updated');
            } else {
                toast.error(response.message || 'Failed to update status');
            }
        } catch (err: any) { toast.error(err.message || 'Error updating status'); }
    };

    const handleDelete = async (registration: TempleRegistration) => {
        const result = await Swal.fire({
            title: 'Delete Registration?',
            text: `Are you sure you want to delete "${registration.temple.name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const response = await adminApiClient.deleteTempleRegistration(registration._id);
                if (response.success) {
                    setRegistrations(prev => prev.filter(r => r._id !== registration._id));
                    toast.success('Deleted successfully');
                    if (selectedRegistration?._id === registration._id) {
                        setShowDetailsModal(false);
                        setSelectedRegistration(null);
                    }
                } else {
                    toast.error(response.message || 'Failed to delete');
                }
            } catch (err: any) { toast.error(err.message || 'Error deleting'); } finally { setLoading(false); }
        }
    };

    const handleReject = async () => {
        if (!selectedRegistration || !rejectionReason.trim()) return;
        try {
            setLoading(true);
            const response = await adminApiClient.rejectTempleRegistration(selectedRegistration._id, { rejectionReason: rejectionReason.trim() });
            if (response.success) {
                fetchRegistrations();
                setShowRejectModal(false);
                setShowDetailsModal(false);
                setRejectionReason('');
                toast.success('Registration rejected');
            } else {
                toast.error(response.message || 'Failed to reject');
            }
        } catch (err: any) { toast.error(err.message || 'Error rejecting'); } finally { setLoading(false); }
    };

    const handleFilterChange = (key: string, value: any) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(p => ({ ...p, pageIndex: 0 }));
    };

    const handleUpdateTemple = async (templeId: string, data: any) => {
        try {
            setLoading(true);
            const response = await adminApiClient.updateTemple(templeId, data);
            if (response.success) {
                toast.success('Temple updated successfully');
                fetchRegistrations(); 
                await fetchTempleDetails(templeId);
                return true;
            } else {
                toast.error(response.message || 'Failed to update temple');
                return false;
            }
        } catch (err: any) {
            toast.error(err.message || 'Error updating temple');
            return false;
        } finally {
            setLoading(false);
        }
    };

    const handleSaveRegistration = async (registrationId: string, data: { status?: string, assignedTo?: string, rejectionReason?: string }) => {
        setLoading(true);
        let success = true;
        try {
            // 1. Handle Assignment
            if (data.assignedTo && data.assignedTo !== selectedRegistration?.assignedTo?._id) {
                const assignRes = await adminApiClient.assignRegistration(registrationId, data.assignedTo);
                if (!assignRes.success) {
                    toast.error(assignRes.message || 'Failed to update assignment');
                    success = false;
                }
            }

            // 2. Handle Status Change
            if (success && data.status && data.status !== selectedRegistration?.status) {
                if (data.status === 'approved') {
                    const approveRes = await adminApiClient.approveTempleRegistration(registrationId);
                    if (!approveRes.success) {
                         toast.error(approveRes.message || 'Failed to approve');
                         success = false;
                    }
                } else if (data.status === 'rejected') {
                    if (!data.rejectionReason) {
                        toast.error('Rejection reason is required for rejection');
                        success = false;
                    } else {
                        const rejectRes = await adminApiClient.rejectTempleRegistration(registrationId, { rejectionReason: data.rejectionReason });
                        if (!rejectRes.success) {
                            toast.error(rejectRes.message || 'Failed to reject');
                            success = false;
                        }
                    }
                } else if (data.status === 'pending') {
                    const pendingRes = await adminApiClient.resetTempleRegistrationToPending(registrationId);
                    if (!pendingRes.success) {
                        toast.error(pendingRes.message || 'Failed to return to pending');
                        success = false;
                    }
                }
            }
            
            if (success) {
                toast.success('Changes saved successfully');
                await fetchRegistrations();
                setShowDetailsModal(false); 
                return true;
            }
        } catch (err: any) {
             const errorMsg = err?.response?.data?.message || err.message || 'Error saving changes';
             toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
        return false;
    };

    return {
        // State
        activeTab, setActiveTab: (tab: any) => { setActiveTab(tab); setPagination(p => ({...p, pageIndex: 0})); },
        registrations,
        loading, error, setError,
        sorting, setSorting,
        pagination, setPagination,
        totalPages, totalRecords,
        filters, handleFilterChange,
        selectedRegistration, setSelectedRegistration,
        showDetailsModal, setShowDetailsModal,
        showRejectModal, setShowRejectModal,
        rejectionReason, setRejectionReason,
        subscriptionPlans,
        templeSubscription,
        templeDetails,
        selectedPlanId, setSelectedPlanId,
        showSubscriptionModal, setShowSubscriptionModal,
        templeId, searchingTemple,
        admins, selectedAdminId, setSelectedAdminId,
        applicationHistory,
        showHistoryModal, setShowHistoryModal,
        fetchingHistory,
        historyTempleName,

        // Actions
        fetchRegistrations,
        handleViewDetails,
        handleViewHistory,
        handleDelete,
        handleAssignSubscription,
        handleRemoveSubscription,
        handleReviewAction,
        handleAssignToMe,
        handleAssignTo,
        handleUpdateVerification,
        handleReject,
        handleUpdateTemple,
        fetchTempleDetails,
        findAndSetTempleId,
        onOpenSubscriptionModal: () => setShowSubscriptionModal(true),
        handleSaveRegistration,
        currentUser: user
    };
};
